import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  AccountBalance as AccountBalanceIcon,
  Delete as DeleteIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import {
  fetchMyBids,
  deleteBid,
  acceptBid,
  selectBids,
  selectBidsLoading,
  selectBidsError
} from '../../store/slices/bidsSlice';
import type { AppDispatch } from '../../store/store';
import type { Bid } from '../../types/bid';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/currency';
import PageBackButton from '../../components/PageBackButton';

const MyBids: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBidId, setSelectedBidId] = useState<number | null>(null);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);

  const bids = useSelector(selectBids);
  const loading = useSelector(selectBidsLoading);
  const error = useSelector(selectBidsError);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchMyBids());
  }, [dispatch]);

  const handleDeleteClick = (bidId: number) => {
    setSelectedBidId(bidId);
    setDeleteDialogOpen(true);
  };

  const handleAcceptClick = (bidId: number) => {
    setSelectedBidId(bidId);
    setAcceptDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedBidId) {
      try {
        await dispatch(deleteBid(selectedBidId)).unwrap();
        setSuccess(t('bids.list.card.delete_success'));
        setDeleteDialogOpen(false);
      } catch (err) {
        // Грешката ще бъде обработена от Redux и показана чрез error state
      }
    }
  };

  const handleAcceptConfirm = async () => {
    if (selectedBidId) {
      try {
        await dispatch(acceptBid(selectedBidId)).unwrap();
        setSuccess(t('bids.list.card.accept_success'));
        setAcceptDialogOpen(false);
      } catch (err) {
        // Грешката ще бъде обработена от Redux и показана чрез error state
      }
    }
  };

  const getStatusColor = (status: Bid['status']) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: Bid['status']) => {
    return t(`bids.status.${status}`);
  };

  const getLocale = () => getDateFnsLocale(i18n.language);

  const sortBids = (bidsToSort: typeof bids) => {
    return [...bidsToSort].sort((a, b) => {
      switch (sort) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'amount_high':
          return b.amount - a.amount;
        case 'amount_low':
          return a.amount - b.amount;
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
  };

  const filteredBids = sortBids(
    bids.filter(bid => {
      console.log('Filtering bid:', bid);
      if (filter !== 'all' && bid.status !== filter) {
        console.log('Filtered out by status');
        return false;
      }
      const matchesSearch = bid.description.toLowerCase().includes(search.toLowerCase());
      console.log('Matches search:', matchesSearch);
      return matchesSearch;
    })
  );

  console.log('Original bids:', bids);
  console.log('Filtered bids:', filteredBids);

  return (
    <Container maxWidth="lg" className="py-8">
      <PageBackButton />
      <Typography variant="h4" component="h1" className="mb-6">
        {t('bids.list.title')}
      </Typography>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper className="p-4 mb-4">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={String(t('bids.list.search'))}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('bids.list.filter.all')}</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label={t('bids.list.filter.all')}
              >
                <MenuItem value="all">{t('bids.list.filter.all')}</MenuItem>
                <MenuItem value="pending">{t('bids.list.filter.pending')}</MenuItem>
                <MenuItem value="accepted">{t('bids.list.filter.accepted')}</MenuItem>
                <MenuItem value="rejected">{t('bids.list.filter.rejected')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('bids.list.sort.newest')}</InputLabel>
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                label={t('bids.list.sort.newest')}
              >
                <MenuItem value="newest">{t('bids.list.sort.newest')}</MenuItem>
                <MenuItem value="oldest">{t('bids.list.sort.oldest')}</MenuItem>
                <MenuItem value="amount_high">{t('bids.list.sort.amount_high')}</MenuItem>
                <MenuItem value="amount_low">{t('bids.list.sort.amount_low')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box className="flex justify-center items-center py-8">
          <CircularProgress />
        </Box>
      ) : filteredBids.length === 0 ? (
        <Typography className="text-center py-8">
          {t('common.no_results')}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredBids.map((bid) => (
            <Grid item xs={12} md={6} key={bid.id}>
              <Card>
                <CardContent>
                  <Box className="flex justify-between items-start mb-2">
                    <Typography variant="h6" component="h2" className="mb-2">
                      {t('bids.list.card.task_id')}: {bid.task_id}
                    </Typography>
                    <Chip
                      label={getStatusText(bid.status)}
                      color={getStatusColor(bid.status)}
                      size="small"
                    />
                  </Box>
                  <Typography
                    color="textSecondary"
                    className="mb-4 line-clamp-2"
                    title={bid.description}
                  >
                    {bid.description}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box className="flex items-center">
                        <AccountBalanceIcon className="mr-2" color="action" />
                        <Typography>
                          {t('bids.list.card.amount')}: {formatCurrency(bid.amount, i18n.language, bid.currency)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box className="flex items-center">
                        <AccessTimeIcon className="mr-2" color="action" />
                        <Typography>
                          {format(new Date(bid.created_at), 'Pp', { locale: getLocale() })}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/tasks/${bid.task_id}`)}
                  >
                    {t('bids.list.card.view_task')}
                  </Button>
                  {bid.status === 'pending' && user?.user_type === 'client' && (
                    <Button
                      size="small"
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={() => handleAcceptClick(bid.id)}
                    >
                      {t('bids.list.card.accept')}
                    </Button>
                  )}
                  {bid.status === 'pending' && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(bid.id)}
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('bids.list.card.delete_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('bids.list.card.delete_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={acceptDialogOpen}
        onClose={() => setAcceptDialogOpen(false)}
      >
        <DialogTitle>{t('bids.list.card.accept_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('bids.list.card.accept_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAcceptConfirm} color="success" autoFocus>
            {t('common.accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyBids; 